
import { defineComponent, ref, onMounted, PropType } from 'vue'
import { repositories } from '@/repositories/system/repositories'
import { object } from 'yup'
import { useForm } from 'vee-validate'
import { myValidationSchemaObj } from '@/views/validation'
import ModalBase from '@/views/modal/ModalBase.vue'
import { callApiWrapper } from '@/views/callApiWrapper'
import { User } from '@/repositories/data'
import { useRoute } from 'vue-router'
import DismissibleAlert from '@/views/components/DismissibleAlert.vue'
import LoadingButton from '@/views/components/LoadingButton.vue'

const userRepository = repositories.user

interface InputForm {
  loginId: string;
  isAdmin: boolean;
}

const validationSchema = object({
  loginId: myValidationSchemaObj.requiredEmail
})

export default defineComponent({
  components: {
    LoadingButton,
    DismissibleAlert,
    ModalBase
  },
  props: {
    isAdd: {
      type: Boolean,
      required: true
    },
    targetObj: {
      type: Object as PropType<User>,
      required: false
    }
  },
  emits: {
    dismiss: () => true,
    updated: () => true
  },
  setup (props, { emit }) {
    const route = useRoute()
    const requestError = ref<string | null>(null)
    const canInitializePassword = ref<boolean>(false)
    const isWaitingInitialize = ref(false)

    const { errors, values, meta, validate } = useForm<InputForm>({
      validationSchema: validationSchema,
      initialValues: {
        loginId: '',
        isAdmin: false
      }
    })

    onMounted(async () => {
      if (props.isAdd) {
        return
      }
      if (!props.targetObj) {
        throw new Error('require target obj.')
      }

      const companyKey = route.params.companyKey as string
      const userKey = props.targetObj.key
      await callApiWrapper(requestError, async () => {
        const user = await userRepository.get(companyKey, userKey)
        values.loginId = user.loginId
        values.isAdmin = user.isAdmin
        await validate()
      })
    })

    const dismiss = () => {
      emit('dismiss')
    }

    const update = async (action: () => Promise<User>) => {
      requestError.value = null
      await callApiWrapper(requestError, async () => {
        await action()
        emit('updated')
      })
    }

    const onClickedAdd = () => {
      const companyKey = route.params.companyKey as string
      update(() => {
        return userRepository.add(companyKey, { loginId: values.loginId, isAdmin: values.isAdmin })
      })
    }

    const onClickedModify = () => {
      if (!props.targetObj) {
        throw new Error('require target obj.')
      }

      const companyKey = route.params.companyKey as string
      const userKey = props.targetObj.key
      update(() => {
        return userRepository.modify(companyKey, userKey, { loginId: values.loginId, isAdmin: values.isAdmin })
      })
    }

    const onClickedInitializePassword = async () => {
      if (!props.targetObj) {
        throw new Error('require target obj.')
      }

      isWaitingInitialize.value = true
      const companyKey = route.params.companyKey as string
      const userKey = props.targetObj.key
      await callApiWrapper(requestError, async () => {
        await userRepository.initializePassword(companyKey, userKey)
        emit('dismiss')
      })
      isWaitingInitialize.value = false
    }

    return {
      requestError,
      canInitializePassword,
      isWaitingInitialize,
      dismiss,
      onClickedAdd,
      onClickedModify,
      onClickedInitializePassword,
      errors,
      values,
      meta,
      validate
    }
  }
})
